/**
 * NextActionCard — "פעולה הבאה" for a lead or a clinic: one textarea plus an
 * optional reminder (date + time, Israel wall clock). A reminder must be in
 * the future; at that time the nivNextActionReminders scheduler pushes the
 * NiV admin who set it (link back to this page).
 *
 * Props:
 *  - value: { text, at, pending, notifiedAt, updatedAt } | null (from the CRM / prospect record)
 *  - onSave({ text, at }): async; `at` is an ISO string or null. Resolves when
 *    persisted, throws on failure (Hebrew message surfaced inline).
 *
 * Explicit save (not autosave): the time needs validation and the push is a
 * side effect the admin should trigger deliberately.
 */

import { useEffect, useMemo, useState } from "react";
import { BellRinging, CheckCircle, FlagBanner, Trash, WarningCircle, X } from "@phosphor-icons/react";
import DatePicker from "../../../../components/ui/DatePicker";
import TimePicker from "../../../../components/ui/TimePicker";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { ISRAEL_TIMEZONE } from "../../../../utils/dateUtils";
import "./NivCrmCards.css";

const pad = (n) => String(n).padStart(2, "0");

/** ISO → { date: "YYYY-MM-DD", time: "HH:MM" } in Israel wall-clock time */
const isoToIsraelParts = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ISRAEL_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
};

/** Israel wall-clock date + time → Date (UTC instant). Handles DST via a two-pass offset fix. */
const israelPartsToDate = (date, time) => {
  if (!date || !time) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;
  const guess = Date.UTC(y, m - 1, d, hh, mm, 0);
  const asIsrael = isoToIsraelParts(new Date(guess).toISOString());
  const [gy, gm, gd] = asIsrael.date.split("-").map(Number);
  const [gh, gmin] = asIsrael.time.split(":").map(Number);
  const seen = Date.UTC(gy, gm - 1, gd, gh, gmin, 0);
  return new Date(guess - (seen - guess));
};

const formatReminder = (iso) => {
  if (!iso) return "";
  const { date, time } = isoToIsraelParts(iso);
  if (!date) return "";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y.slice(2)} ${time}`;
};

const formatSavedTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function NextActionCard({ value, onSave, placeholder = "מה הצעד הבא עם הלקוח? (תזכורת אופציונלית)" }) {
  const initial = useMemo(() => {
    const parts = isoToIsraelParts(value?.at);
    return { text: value?.text || "", date: parts.date, time: parts.time };
  }, [value?.text, value?.at]);

  const [text, setText] = useState(initial.text);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [error, setError] = useState("");

  // External value changed (refetch / other device) — adopt it when not mid-edit
  useEffect(() => {
    setText(initial.text);
    setDate(initial.date);
    setTime(initial.time);
    setStatus("idle");
    setError("");
  }, [initial]);

  const dirty = text !== initial.text || date !== initial.date || time !== initial.time;
  const hasAnything = !!text.trim() || !!date || !!time;

  // Live validation: the reminder must be a complete, future, Israel-time instant
  const reminder = useMemo(() => {
    if (!date && !time) return { at: null, error: "" };
    if (!date || !time) return { at: null, error: "יש לבחור גם תאריך וגם שעה לתזכורת" };
    const at = israelPartsToDate(date, time);
    if (!at) return { at: null, error: "מועד התזכורת אינו תקין" };
    if (at.getTime() <= Date.now()) return { at: null, error: "מועד התזכורת חייב להיות בעתיד" };
    return { at, error: "" };
  }, [date, time]);

  const canSave = dirty && !reminder.error && status !== "saving";

  const persist = async (payload) => {
    setStatus("saving");
    setError("");
    try {
      await onSave(payload);
      setStatus("saved");
    } catch (err) {
      setStatus("error");
      setError(err?.message || "השמירה נכשלה");
    }
  };

  const handleSave = () => {
    if (!canSave) return;
    persist({ text: text.trim(), at: reminder.at ? reminder.at.toISOString() : null });
  };

  const handleClearReminder = () => {
    setDate("");
    setTime("");
    setStatus("idle");
  };

  const handleClearAll = () => {
    setText("");
    setDate("");
    setTime("");
    if (value?.text || value?.at) persist({ text: "", at: null });
  };

  const showPending = !dirty && value?.pending && value?.at;
  const showNotified = !dirty && !value?.pending && value?.notifiedAt;

  return (
    <section className="nlc-card nlc-next">
      <header className="nlc-card-head">
        <span className="nlc-card-icon nlc-card-icon-next">
          <FlagBanner size={15} weight="bold" />
        </span>
        <div className="nlc-card-titles">
          <span className="nlc-card-eyebrow">פנימי</span>
          <h3 className="nlc-card-title">פעולה הבאה</h3>
        </div>
        <span className={`nlc-save-state is-${status}`} aria-live="polite">
          {status === "saving" && (
            <>
              <LoaderLoop size="xs" /> שומר…
            </>
          )}
          {status === "saved" && (
            <>
              <CheckCircle size={12} weight="fill" /> נשמר
            </>
          )}
          {status === "error" && (
            <>
              <WarningCircle size={12} weight="fill" /> השמירה נכשלה
            </>
          )}
          {status === "idle" && dirty && "לא נשמר"}
          {status === "idle" && !dirty && value?.updatedAt && `עודכן ${formatSavedTime(value.updatedAt)}`}
        </span>
      </header>

      {showPending && (
        <div className="nlc-next-banner is-pending">
          <BellRinging size={13} weight="fill" />
          <span>תזכורת תישלח ב-{formatReminder(value.at)}</span>
        </div>
      )}
      {showNotified && (
        <div className="nlc-next-banner is-done">
          <CheckCircle size={13} weight="fill" />
          <span>התזכורת נשלחה ב-{formatReminder(value.notifiedAt)}</span>
        </div>
      )}

      <textarea
        className="nlc-textarea nlc-textarea-sm"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setStatus("idle");
        }}
        placeholder={placeholder}
        rows={3}
        aria-label="פעולה הבאה"
      />

      <div className="nlc-next-when">
        <span className="nlc-next-when-label">
          <BellRinging size={13} /> תזכורת
        </span>
        <div className="nlc-next-pickers">
          <DatePicker
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setStatus("idle");
            }}
            placeholder="תאריך"
            error={!!reminder.error}
          />
          <TimePicker
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setStatus("idle");
            }}
            placeholder="שעה"
            error={!!reminder.error}
          />
          {(date || time) && (
            <button type="button" className="nlc-icon-btn" onClick={handleClearReminder} aria-label="בטל תזכורת" title="בטל תזכורת">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {(reminder.error || error) && (
        <div className="nlc-next-error" role="alert">
          <WarningCircle size={13} weight="fill" />
          {reminder.error || error}
        </div>
      )}

      <div className="nlc-next-actions">
        <button type="button" className="nlc-primary-btn" onClick={handleSave} disabled={!canSave}>
          {reminder.at ? "שמור ותזכר אותי" : "שמור"}
        </button>
        {hasAnything && (
          <button type="button" className="nlc-ghost-btn" onClick={handleClearAll} disabled={status === "saving"}>
            <Trash size={13} /> נקה
          </button>
        )}
      </div>
    </section>
  );
}

export default NextActionCard;
